/**
 * Product-updates list: people who opted in from the tier quiz or the public preferences page.
 */

import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem,
  Pagination,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  MailOutline as MailOutlineIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { adminService } from '../../services/adminService';
import { MarketingSubscription, MarketingSubscriptionFilters } from '../../types/admin';
import { formatDate } from '../../utils/formatters';

const MarketingSubscriptions: React.FC = () => {
  const [rows, setRows] = useState<MarketingSubscription[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [filters, setFilters] = useState<MarketingSubscriptionFilters>({
    page: 1,
    page_size: 25,
    status: '',
  });
  const [emailFilter, setEmailFilter] = useState('');
  const [selected, setSelected] = useState<MarketingSubscription | null>(null);

  const loadRows = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await adminService.getMarketingSubscriptions({
        ...filters,
        email: emailFilter || undefined,
        status: filters.status || undefined,
      });
      setRows(result.subscriptions);
      setTotalCount(result.total_count);
    } catch (err: any) {
      setError(err?.message || 'Failed to load the marketing list');
    } finally {
      setLoading(false);
    }
  }, [filters, emailFilter]);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await loadRows();
    } finally {
      setRefreshing(false);
    }
  };

  const changeStatus = async (status: 'subscribed' | 'unsubscribed') => {
    if (!selected) return;
    setSaving(true);
    setError(null);
    try {
      const updated = await adminService.updateMarketingSubscriptionStatus(selected.id, status);
      setSelected(updated);
      await loadRows();
    } catch (err: any) {
      setError(err?.message || 'Failed to update this address');
    } finally {
      setSaving(false);
    }
  };

  const pageCount = Math.max(1, Math.ceil(totalCount / (filters.page_size ?? 25)));

  const displayName = (row: MarketingSubscription) => row.first_name || '—';

  if (loading && rows.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" display="flex" alignItems="center" gap={1}>
          <MailOutlineIcon />
          Marketing list
        </Typography>
        <Tooltip title="Refresh">
          <IconButton onClick={handleRefresh} disabled={refreshing} color="primary">
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Addresses that asked for product updates. Tier-quiz results are under Access Requests.
        Unsubscribing here stops future updates and does not delete the quiz submission.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                size="small"
                label="Filter by email"
                value={emailFilter}
                onChange={(e) => {
                  setEmailFilter(e.target.value);
                  setFilters((f) => ({ ...f, page: 1 }));
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                select
                fullWidth
                size="small"
                label="Status"
                value={filters.status || ''}
                onChange={(e) =>
                  setFilters((f) => ({
                    ...f,
                    page: 1,
                    status: e.target.value as MarketingSubscriptionFilters['status'],
                  }))
                }
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="subscribed">Subscribed</MenuItem>
                <MenuItem value="unsubscribed">Unsubscribed</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Typography variant="body2" color="text.secondary">
                {totalCount} address{totalCount === 1 ? '' : 'es'}
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Updated</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Source</TableCell>
              <TableCell>Opted in</TableCell>
              <TableCell>Opted out</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography variant="body2" color="text.secondary" py={2}>
                    No marketing subscribers yet.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row) => (
                <TableRow
                  key={row.id}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => setSelected(row)}
                >
                  <TableCell>{formatDate(row.updated_at)}</TableCell>
                  <TableCell>{displayName(row)}</TableCell>
                  <TableCell>{row.email}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={row.status === 'subscribed' ? 'Subscribed' : 'Unsubscribed'}
                      color={row.status === 'subscribed' ? 'success' : 'default'}
                    />
                  </TableCell>
                  <TableCell>{row.source || '—'}</TableCell>
                  <TableCell>{row.opted_in_at ? formatDate(row.opted_in_at) : '—'}</TableCell>
                  <TableCell>{row.opted_out_at ? formatDate(row.opted_out_at) : '—'}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {pageCount > 1 && (
        <Box display="flex" justifyContent="center" mt={3}>
          <Pagination
            count={pageCount}
            page={filters.page ?? 1}
            onChange={(_, page) => setFilters((f) => ({ ...f, page }))}
            color="primary"
          />
        </Box>
      )}

      <Dialog
        open={Boolean(selected)}
        onClose={() => setSelected(null)}
        maxWidth="sm"
        fullWidth
      >
        {selected && (
          <>
            <DialogTitle>{selected.email}</DialogTitle>
            <DialogContent dividers>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Name:</strong> {displayName(selected)}
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Status:</strong>{' '}
                {selected.status === 'subscribed' ? 'Subscribed' : 'Unsubscribed'}
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Source:</strong> {selected.source || '—'}
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Opted in:</strong>{' '}
                {selected.opted_in_at ? formatDate(selected.opted_in_at) : '—'}
              </Typography>
              <Typography variant="body2" sx={{ mb: 2 }}>
                <strong>Opted out:</strong>{' '}
                {selected.opted_out_at ? formatDate(selected.opted_out_at) : '—'}
              </Typography>
              <Box display="flex" gap={1}>
                {selected.status === 'subscribed' ? (
                  <Button
                    variant="contained"
                    color="warning"
                    disabled={saving}
                    onClick={() => changeStatus('unsubscribed')}
                  >
                    Unsubscribe
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    disabled={saving}
                    onClick={() => changeStatus('subscribed')}
                  >
                    Resubscribe
                  </Button>
                )}
              </Box>
            </DialogContent>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default MarketingSubscriptions;
